import {
  EngageCommunicationListItem,
  EngageFlatContact,
  EngageFlatListItem,
  EngageFlatsDefaultFilter,
  EngagePropertiesDefaultFilter,
  EngageStore,
  FlatContactType,
} from 'types/Engage';
import { PageEnum } from './types';
import { sortBy } from 'lodash';
import { differenceInCalendarDays } from 'date-fns';
import { isoDateStringToDate } from 'lib/riskbase';

export function getDefaultEngageData(): EngageStore {
  return {
    type: PageEnum.engage,
    data: {
      isWaitingInitLoad: true,
      isLoading: false,
      properties: {
        totalCount: 0,
        pageNumber: 1,
        dataOrder: [],
        data: {},
        filters: { ...EngagePropertiesDefaultFilter },
      },
      flats: {
        totalCount: 0,
        pageNumber: 1,
        dataOrder: [],
        data: {},
        filters: { ...EngageFlatsDefaultFilter },
      },
      scheduledVisitTemplates: {
        status: 'notloaded',
        templates: [],
      },
      scheduledVisits: {
        totalCount: 0,
        data: {},
        dataOrder: [],
      },
      scheduledVisitRecipients: {
        loading: false,
        data: null,
      },
      flat: null,
      bulkCommunication: null,
    },
  };
}

export function sortContactsByType<
  T extends Pick<
    EngageFlatContact,
    'isOwner' | 'isResident' | 'isUnder16' | 'contactType'
  > & { firstName?: string; lastName?: string; name?: string },
>(contacts: T[]): T[] {
  return sortBy(
    contacts,
    (contact) =>
      (contact.isOwner
        ? !contact.isResident
          ? 1 // owner
          : 2 // owner & resident
        : contact.isResident
          ? !contact.isUnder16
            ? 3 // resident over 16
            : 4 // resident under 16
          : contact.contactType === FlatContactType.onBehalfOf
            ? 5
            : contact.contactType === FlatContactType.lettingAgent
              ? 6
              : 7) +
      (contact.name || `${contact.firstName} ${contact.lastName}`),
  );
}

export function sortCommunicationsByDateReversed(
  communications: EngageCommunicationListItem[],
): EngageCommunicationListItem[] {
  return sortBy(
    communications,
    (communication) => communication.createdDate,
  ).reverse();
}

export function flatContactToFlatListContact(
  contact: EngageFlatContact,
): EngageFlatListItem['contacts'][number] {
  return {
    id: contact.id,
    name: `${contact.firstName} ${contact.lastName || ''}`.trim(),
    email: contact.email,
    phone: contact.phone,
    isOwner: contact.isOwner,
    isResident: contact.isResident,
    isPrimary: !!contact.isPrimary,
    isUnder16: contact.isUnder16,
    hasSupportNeeds: contact.hasSupportNeeds,
    contactType: contact.contactType,
    confirmedDate: contact.confirmedDate,
    confirmationExpiryDate: contact.confirmationExpiryDate,
  };
}

export function getFlatContactConfirmationInfo(
  contact: Pick<
    EngageFlatListItem['contacts'][number],
    'confirmedDate' | 'confirmationExpiryDate'
  >,
): { confirmedAndInDate: boolean; daysUntilExpiry: number | null } {
  const daysUntilExpiry = contact.confirmationExpiryDate
    ? differenceInCalendarDays(
        isoDateStringToDate(contact.confirmationExpiryDate),
        new Date(),
      )
    : null;
  return {
    confirmedAndInDate:
      !!contact.confirmedDate &&
      (daysUntilExpiry === null || daysUntilExpiry >= 0),
    daysUntilExpiry,
  };
}
